import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { WooComponentsModule } from '../../../scripts/woo_components.module/module';
import { WooTabComponent } from '../../../src/shared/navigation/woo-tabs/woo-tab/woo-tab.component';
import { WooTabsComponent } from '../../../src/shared/navigation/woo-tabs/woo-tabs.component';
import { compareWith } from '../../utils/object';
import { UserRole } from '../../utils/user-roles';
import { AccountService } from '../../woo_services.module/AccountService';
import { AgencyService } from '../../woo_services.module/AgencyService';
import { AuthService } from '../../woo_services.module/AuthService';
import { CustomerService } from '../../woo_services.module/CustomerService';
import { CompactAgency, CompactCustomer, CompactUser } from '../../woo_services.module/shared-types';
import { TranslationService } from '../../woo_services.module/TranslationService';
import { UserService } from '../../woo_services.module/UserService';
import { NameList } from './name_list/name-list.component';
import { UserList } from './user_list/user-list.component';

enum Tab {
  AgencyCustomers = 'agency_customers',
  Agencies = 'agencies',
  DirectCustomers = 'customers',
  Users = 'users',
}

@Component({
  selector: 'manage-accounts',
  imports: [WooTabComponent, WooTabsComponent, CommonModule, NameList, UserList, RouterLink, WooComponentsModule],
  templateUrl: './manage-accounts.component.html',
})
export class ManageAccounts implements OnInit {
  readonly translationService = inject(TranslationService);
  readonly authService = inject(AuthService);
  readonly Tab = Tab;
  readonly translateRoles = this.translationService.convertRoles;
  readonly isExternalPlanner = this.authService.hasRole(UserRole.externalPlanner);
  readonly isAdminOrExternalPlanner = this.authService.hasAnyRole([UserRole.admin, UserRole.externalPlanner]);

  activeUsers: CompactUser[] = [];
  inactiveUsers: CompactUser[] = [];
  agencies: CompactAgency[] = [];
  clients: CompactCustomer[] = [];
  agencyCustomers: CompactCustomer[] = [];
  inactiveAgencyCustomers: CompactCustomer[] = [];
  defaultTab: string;

  hasLoaded = {
    agencies: false,
    agencyCustomers: false,
    clients: false,
    users: false,
  };

  show = {
    inactiveUsers: false,
    nicknames: this.authService.adminOrPlanner(),
  };

  constructor(
    private userService: UserService,
    private agencyService: AgencyService,
    private accountService: AccountService,
    private customerService: CustomerService,
  ) {}

  ngOnInit(): void {
    if (this.authService.hasRole(UserRole.externalPlanner)) {
      this.defaultTab = Tab.Users;
    } else if (this.authService.hasRole(UserRole.client)) {
      this.defaultTab = Tab.DirectCustomers;
    } else {
      this.defaultTab = Tab.AgencyCustomers;
    }

    this.userService.getUsers().then((users) => {
      const compare = compareWith('first_name');
      this.activeUsers = users.filter((user) => user.allowed_to_sign_in).sort(compare);
      this.inactiveUsers = users.filter((user) => !user.allowed_to_sign_in).sort(compare);
      this.hasLoaded.users = true;
    });

    this.agencyService.getAgencies().then((agencies) => {
      this.agencies = agencies.sort(compareWith('name'));
      this.hasLoaded.agencies = true;
    });

    this.accountService.getAgencyCustomers().then((agencyCustomers) => {
      const compare = compareWith('name');
      this.agencyCustomers = agencyCustomers.filter((c) => c.active_customer).sort(compare);
      this.inactiveAgencyCustomers = agencyCustomers.filter((c) => !c.active_customer).sort(compare);
      this.hasLoaded.agencyCustomers = true;
    });

    this.customerService.getClientsForCurrentUser().then((clients) => {
      this.clients = clients.sort(compareWith('name'));
      this.hasLoaded.clients = true;
    });
  }

  hasAccessToTab(tab: Tab): boolean {
    switch (tab) {
      case Tab.AgencyCustomers:
        return this.authService.hasAnyRole([
          UserRole.admin,
          UserRole.planner,
          UserRole.accounting,
          UserRole.agencyAdmin,
          UserRole.sales,
          UserRole.salesManager,
          UserRole.agencyUser,
        ]);
      case Tab.Agencies:
        return this.authService.hasAnyRole([
          UserRole.admin,
          UserRole.planner,
          UserRole.accounting,
          UserRole.agencyAdmin,
          UserRole.sales,
          UserRole.salesManager,
          UserRole.agencyUser,
        ]);
      case Tab.DirectCustomers:
        return this.authService.hasAnyRole([
          UserRole.admin,
          UserRole.planner,
          UserRole.accounting,
          UserRole.sales,
          UserRole.salesManager,
          UserRole.client,
        ]);
      case Tab.Users:
        return this.authService.hasAnyRole([UserRole.admin, UserRole.planner, UserRole.externalPlanner]);
      default:
        return false;
    }
  }
}
